import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useHistory, useParams } from "react-router-dom";

import { Button } from "Components/Button";
import PageHeader from "Components/Pages/Header";
import PageWrapper from "Components/Pages/Wrapper";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { LoadingText } from "Components/LoadingText";
import { UnitsRegionPicker } from "Components/Units/UnitsRegionPicker";

import { getUnitHoliday } from "Requests/Units";

import { createToast } from "Utils/toastFunc";

import { Box } from "styles/globalComponents";
import { formatDateToBR } from "Utils/Date";
import { useRegions } from "Hooks/useRegions";
import { getInitialUnitsValue } from "Utils/Units/Units";
import { DetailContainer } from "./style";

export type UnitHolidayDetailData = {
  id: number;
  description: string;
  holiday: string;
  scope: string;
  is_fixed?: boolean;
  city_id?: number | null;
  city?: string | null;
  units?: {
    include: number[];
    exclude: number[];
  };
};

function scopeLabel(scope: string | undefined) {
  if (scope === "national") return "Nacional";
  if (scope === "municipal") return "Municipal";
  if (scope === "state") return "Estadual";
  return scope ?? "—";
}

function noopSelection() {
  return undefined;
}

export function UnitsHolidayDetail() {
  const history = useHistory();
  const { holidayId } = useParams<{ holidayId: string }>();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<UnitHolidayDetailData | null>(null);

  const { regions, loadingRegions, regionsError } = useRegions();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const res = await getUnitHoliday(holidayId);
        const data = res?.data as UnitHolidayDetailData | undefined;
        if (!cancelled && data) setDetail(data);
      } catch {
        if (!cancelled) {
          createToast("error", "Não foi possível carregar o feriado.");
          history.push("/unidades/disponibilidade");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (holidayId) load();

    return () => {
      cancelled = true;
    };
  }, [holidayId, history]);

  return (
    <PageWrapper className="entry-app-content">
      <div className="content-holder">
        <div className="page-heading">
          <div className="wrapper-header-content">
            <Box>
              <PageHeader title="Detalhes do feriado">
                <Breadcrumb
                  list={[
                    {
                      path: "/unidades",
                      label: "Unidades",
                      current: false,
                    },
                    {
                      path: "/unidades/disponibilidade",
                      label: "Disponibilidade",
                      current: false,
                    },
                    {
                      path: "#",
                      label: "Detalhes",
                      current: true,
                    },
                  ]}
                />
              </PageHeader>
            </Box>

            <Box display="flex" gap="10px" justify="flex-end">
              <Button
                type="button"
                outlined
                borderRadius="111"
                onClick={() => history.push("/unidades/disponibilidade")}
              >
                Voltar
              </Button>
              {detail && (
                <Button
                  type="button"
                  borderRadius="111"
                  onClick={() =>
                    history.push(
                      `/unidades/disponibilidade/${detail.id}/editar`
                    )
                  }
                >
                  Editar
                </Button>
              )}
            </Box>
          </div>
        </div>

        <DetailContainer className="page-content">
          {loading && <LoadingText text="Carregando feriado" />}
          {!loading && detail && (
            <Box className="md-12" pl="0" pr="0">
              <p>
                <strong>Descrição:</strong> {detail.description}
              </p>
              <p>
                <strong>Data:</strong> {formatDateToBR(detail.holiday)}
              </p>
              <p>
                <strong>Escopo:</strong> {scopeLabel(detail.scope)}
              </p>
              <p>
                <strong>Feriado com data fixa:</strong>{" "}
                {detail.is_fixed ? "Sim" : "Não"}
              </p>
            </Box>
          )}
          {!loading && detail && (
            <Formik
              enableReinitialize
              initialValues={{
                units: getInitialUnitsValue(detail.units, regions),
              }}
              onSubmit={noopSelection}
            >
              <UnitsRegionPicker
                regions={regions}
                loadingRegions={loadingRegions}
                regionsError={regionsError}
                onSelectAllUnits={noopSelection}
                onSelectRegion={noopSelection}
                onSelectUnit={noopSelection}
                readOnly
              />
            </Formik>
          )}
        </DetailContainer>
      </div>
    </PageWrapper>
  );
}
